import "./btn-workspace-dropdown.css"
import cn from "classnames"
import { useTranslation } from "@nova/use-translation"
import React, { useEffect, useRef } from "react"
import * as Icons from "@nova/component-icons"
import { createPortal } from "react-dom"
import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import { zIndex } from "@nova/constants"

type DropdownButton = {
    label: string
    subLabel?: string
    onClick: () => void
    icon?: Icons.IconComponent
    className?: string
    disabled?: boolean
}

type ButtonSplitProps = React.HTMLAttributes<HTMLElement> & {
    dropdownButtons: DropdownButton[]
    label: string
    labelIcon?: Icons.IconComponent
    disabled?: boolean
    menuOpen: boolean
    widthClass: string
    dropdownWidthClass?: string
    setMenuOpen: (open: boolean) => void
    canOpenMenuWhenDisabled?: boolean
    maxDropdownHeight?: number
    flipMenuDropdown?: boolean
    isTransparentButton?: boolean
    //use portalId to render the dropdown container in a different area of the DOM, useful for getting it outside of scrollable overflow containers
    portalPositioning?: {
        portalId: string
        relativeOffset: number
    }
    isSmall?: boolean
}

/**
 *
 * Restyled ButtonSplit introduced with Workspace Next Steps
 */
export const ButtonWorkspaceDropdown = ({
    label,
    labelIcon,
    className,
    dropdownButtons,
    disabled,
    menuOpen,
    widthClass,
    dropdownWidthClass,
    setMenuOpen,
    isTransparentButton,
    style,
    maxDropdownHeight,
    flipMenuDropdown,
    portalPositioning,
    isSmall,
    ...rest
}: ButtonSplitProps) => {
    const { t } = useTranslation()
    const containerRef = useRef<HTMLDivElement>(null)

    const container = containerRef.current
    const b = container?.getBoundingClientRect()
    const portal = document.getElementById(portalPositioning?.portalId || "")

    useEffect(() => {
        const closeDropdownOnScroll = () => {
            setMenuOpen?.(false)
        }
        if (menuOpen) {
            document.addEventListener("wheel", closeDropdownOnScroll, { once: true })
        }
        return () => {
            document.removeEventListener("wheel", closeDropdownOnScroll)
        }
    }, [menuOpen, setMenuOpen])

    return (
        <ClickAwayListenerV2
            portalId={portalPositioning?.portalId}
            onClickAway={() => setMenuOpen(false)}
            style={
                widthClass === "w-full"
                    ? {
                          width: "100%",
                      }
                    : {}
            }
        >
            <div
                ref={containerRef}
                className={cn(
                    "h-[2.5rem] button-workspace-dropdown--container bg-gray-700 cursor-pointer rounded-lg text-gray-0",
                    {
                        "!text-gray-300 !cursor-not-allowed": disabled,
                        "!bg-transparent": isTransparentButton,
                        "!h-[2rem]": isSmall,
                    },
                    widthClass,
                    className
                )}
                onClick={() => {
                    if (!disabled) setMenuOpen(!menuOpen)
                }}
                style={style}
                {...rest}
            >
                <div
                    className={cn(
                        "flex w-full justify-between items-center p-2 pl-4 text-grey-0 border-[0.063rem] border-transparent rounded-lg overflow-hidden whitespace-nowrap",
                        { "border-blue-200": menuOpen }
                    )}
                >
                    <div className="flex items-center gap-3 w-full">
                        {labelIcon && (
                            <Icons.SmallIcon Icon={labelIcon} className="text-gray-100" />
                        )}
                        {!isSmall && (
                            <h5 className="button-workspace-dropdown capitalize max-w-[90%] overflow-hidden text-ellipsis select-none">
                                {t(label)}
                            </h5>
                        )}
                        {isSmall && (
                            <small className="button-workspace-dropdown capitalize max-w-[90%] overflow-hidden text-ellipsis select-none">
                                {t(label)}
                            </small>
                        )}
                    </div>
                    <Icons.ArrowDropDownIcon
                        dropdownOpen={menuOpen}
                        setDropdownOpen={setMenuOpen}
                        disabled={disabled}
                    />
                </div>
                {portal &&
                    createPortal(
                        <DropdownComponent
                            menuOpen={menuOpen}
                            flipMenuDropdown={flipMenuDropdown}
                            dropdownButtons={dropdownButtons}
                            maxDropdownHeight={maxDropdownHeight}
                            setMenuOpen={setMenuOpen}
                            widthClass={dropdownWidthClass || widthClass}
                            style={{
                                top: (b?.top || 0) + (portalPositioning?.relativeOffset || 0),
                                left: b?.left,
                                position: "fixed",
                            }}
                            isSmall={isSmall}
                        />,
                        portal as HTMLElement
                    )}
                {!portalPositioning && (
                    <DropdownComponent
                        menuOpen={menuOpen}
                        flipMenuDropdown={flipMenuDropdown}
                        dropdownButtons={dropdownButtons}
                        maxDropdownHeight={maxDropdownHeight}
                        widthClass={dropdownWidthClass || widthClass}
                        setMenuOpen={setMenuOpen}
                        isSmall={isSmall}
                    />
                )}
            </div>
        </ClickAwayListenerV2>
    )
}

const DropdownComponent = ({
    menuOpen,
    flipMenuDropdown,
    maxDropdownHeight,
    dropdownButtons,
    widthClass,
    style,
    isSmall,
    setMenuOpen,
}: Omit<ButtonSplitProps, "label" | "onClick">) => {
    const { t } = useTranslation()
    return (
        <div
            className={cn(
                "absolute top-10 left-0 w-full flex flex-col items-start rounded-md bg-gray-700 py-1 mt-0.5",
                {
                    visible: menuOpen,
                    hidden: !menuOpen,
                    "button-workspace-dropdown--menu--flipped": flipMenuDropdown,
                    "!top-[30px]": isSmall,
                },
                widthClass
            )}
            style={{
                maxHeight: maxDropdownHeight,
                ...style,
                boxShadow: "0 2px 10px 0 rgba(12, 12, 15, 0.95)" /* shadow-context-menu */,
                zIndex: zIndex.HighPriorityModalDropdown,
            }}
        >
            {dropdownButtons.map(
                (
                    {
                        label,
                        subLabel,
                        onClick: dropdownOnClick,
                        disabled: buttonDisabled,
                        className,
                        icon,
                    },
                    idx
                ) => {
                    return (
                        <button
                            key={idx}
                            className={cn(
                                "button-workspace-dropdown--dropdown-button text-gray-0 w-full flex items-center gap-3 text-left pl-4 pr-3 py-2 my-0.5 hover:bg-gray-500 ",
                                { "!text-gray-300 cursor-not-allowed": buttonDisabled },
                                className
                            )}
                            disabled={buttonDisabled}
                            onClick={() => {
                                dropdownOnClick()
                                setMenuOpen(false)
                            }}
                        >
                            {icon && <Icons.SmallIcon Icon={icon} className="text-gray-100" />}
                            <div className="flex justify-between items-center w-full">
                                {!isSmall && (
                                    <h5 className="overflow-hidden whitespace-nowrap overflow-ellipsis capitalize select-none">
                                        {t(label)}
                                    </h5>
                                )}
                                {isSmall && (
                                    <small className="overflow-hidden whitespace-nowrap overflow-ellipsis capitalize select-none">
                                        {t(label)}
                                    </small>
                                )}
                                {subLabel && <small className="text-gray-400">{t(subLabel)}</small>}
                            </div>
                        </button>
                    )
                }
            )}
        </div>
    )
}
